// ─── Local Cart Utilities ─────────────────────────────────────────────────────

export interface LocalCartBadges {
  isNew: boolean;
  isTrending: boolean;
  isFeatured: boolean;
  isBestSeller: boolean;
}

export interface LocalCartItem {
  cartItemId: number;   // negative timestamp-based id for local items
  productId:  number;
  variantId?: number | null;
  title:      string;
  sku:        string | null;
  color:      string | null;
  size:       string | null;
  price:      number;
  salePrice:  number | null;
  discountPercent?: number;
  quantity:   number;
  lineTotal:  number;
  image:      string | null;
  colorImage?:    string | null;
  selectedImage?: string | null;
  gallery?:       string[];
  inStock:    boolean;
  stock?:     number;
  variantHex?: string | null;
  brand?:      string | null;
  rating?:     number | null;
  reviewCount?: number | null;
  material?:   string | null;
  slug?:       string | null;
  delivery?:     string;
  returnPolicy?: string;
  badges?: LocalCartBadges;
}

const KEY = "mk_cart";

export function getLocalCart(): LocalCartItem[] {
  if (typeof window === "undefined") return [];
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? "[]") as LocalCartItem[];
  } catch {
    return [];
  }
}

export function saveLocalCart(items: LocalCartItem[]): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(KEY, JSON.stringify(items));
}

/**
 * Adds a product to the guest cart, or merges into an existing line if the
 * same product + color + size already exists (mirrors the server's
 * /cart/add merge rule). Returns the cartItemId of the affected line so
 * callers can look it up immediately after.
 */
export function addToLocalCart(
  item: Omit<LocalCartItem, "cartItemId" | "lineTotal">
): number {
  const cart = getLocalCart();
  const idx  = cart.findIndex(
    (i) =>
      i.productId === item.productId &&
      (i.color ?? "") === (item.color ?? "") &&
      (i.size  ?? "") === (item.size  ?? "")
  );
  const effectivePrice = item.salePrice ?? item.price;

  if (idx !== -1) {
    cart[idx] = {
      ...cart[idx],
      ...item,
      quantity: cart[idx].quantity + item.quantity,
    };
    cart[idx].lineTotal = cart[idx].quantity * (cart[idx].salePrice ?? cart[idx].price);
    saveLocalCart(cart);
    return cart[idx].cartItemId;
  }

  const cartItemId = -(Date.now());
  cart.push({ ...item, cartItemId, lineTotal: item.quantity * effectivePrice });
  saveLocalCart(cart);
  return cartItemId;
}

/** Quantity-only update (used by the +/- steppers on the cart page). */
export function updateLocalCartItem(cartItemId: number, qty: number): void {
  const cart = getLocalCart().map((i) => {
    if (i.cartItemId !== cartItemId) return i;
    const ep = i.salePrice ?? i.price;
    return { ...i, quantity: qty, lineTotal: qty * ep };
  });
  saveLocalCart(cart);
}

/**
 * Full variant update (color/size/qty/price/images/stock/etc.) — used by
 * CartProductModal's "Update Cart" action for guest items. Returns the
 * merged item, or null if the id wasn't found.
 */
export function updateLocalCartVariant(
  cartItemId: number,
  updates: Partial<Omit<LocalCartItem, "cartItemId" | "lineTotal">>
): LocalCartItem | null {
  const cart = getLocalCart();
  const idx  = cart.findIndex((i) => i.cartItemId === cartItemId);
  if (idx === -1) return null;

  const merged: LocalCartItem = { ...cart[idx], ...updates };
  const ep = merged.salePrice ?? merged.price;
  merged.lineTotal = merged.quantity * ep;

  cart[idx] = merged;
  saveLocalCart(cart);
  return merged;
}

export function removeLocalCartItem(cartItemId: number): void {
  saveLocalCart(getLocalCart().filter((i) => i.cartItemId !== cartItemId));
}

export function localCartCount(): number {
  return getLocalCart().reduce((s, i) => s + i.quantity, 0);
}

export function clearLocalCart(): void {
  if (typeof window === "undefined") return;
  localStorage.removeItem(KEY);
}